import React, { useState, useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import { ChevronLeftIcon, ChevronRightIcon, Bars3Icon, SunIcon, MoonIcon } from "@heroicons/react/24/outline";
import NotificationDropdown from "../../feedback/NotificationDropdown";
import ProfileDropdown from "../../../../features/users/components/ProfileDropdown";
import { toggleDarkMode, selectDarkMode } from "../../../../app/store/slices/uiSlice";

const VaccineHeader = ({ collapsed, setCollapsed, activeSection }) => {
  const dispatch = useDispatch();
  const darkMode = useSelector(selectDarkMode);
  const [scrolled, setScrolled] = useState(false);
  const [isAnimating, setIsAnimating] = useState(false);

  // Apply dark mode class on mount and when darkMode changes
  useEffect(() => {
    if (darkMode) {
      document.documentElement.classList.add('dark');
    } else {
      document.documentElement.classList.remove('dark');
    }
  }, [darkMode]);

  // Detect scroll for shadow animation
  useEffect(() => {
    const handleScroll = () => {
      setScrolled(window.scrollY > 10);
    };
    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  const handleToggle = () => {
    setIsAnimating(true);
    setCollapsed(!collapsed);
    setTimeout(() => setIsAnimating(false), 300);
  };

  const handleDarkModeToggle = () => {
    dispatch(toggleDarkMode());
  };

  return (
    <header
      className={`
        rounded-2xl mx-4 lg:mx-3 fixed top-3 h-16
        flex items-center justify-between px-4 sm:px-6 z-20
        transition-all duration-300 ease-out
        ${darkMode ? 'bg-gray-800 border-gray-700' : 'bg-white border-gray-100'}
        border
        ${collapsed ? "lg:left-[5.5rem] lg:w-[calc(100%-7.5rem)]" : "lg:left-[18rem] lg:w-[calc(100%-20rem)]"}
        left-0 w-[calc(100%-2rem)]
        ${scrolled
          ? darkMode
            ? 'shadow-lg shadow-black/20'
            : 'shadow-lg shadow-gray-200/50'
          : darkMode
            ? 'shadow-md shadow-black/10'
            : 'shadow-md shadow-gray-100/50'
        }
      `}
    >
      <div className="flex items-center space-x-4">
        {/* Desktop toggle with rotation animation */}
        <button
          onClick={handleToggle}
          className={`
            hidden lg:flex p-2.5 rounded-xl
            transition-all duration-200 ease-out
            group
            ${darkMode
              ? 'hover:bg-gray-700 text-gray-400 hover:text-white'
              : 'hover:bg-gray-100 text-gray-500 hover:text-gray-700'
            }
          `}
        >
          <div className={`transition-transform duration-300 ${isAnimating ? 'scale-90' : 'scale-100'}`}>
            {collapsed ? (
              <ChevronRightIcon className="h-5 w-5 transition-colors" />
            ) : (
              <ChevronLeftIcon className="h-5 w-5 transition-colors" />
            )}
          </div>
        </button>

        {/* Mobile toggle with bounce animation */}
        <button
          onClick={handleToggle}
          className={`
            lg:hidden p-2.5 rounded-xl
            transition-all duration-200
            group
            ${darkMode
              ? 'hover:bg-gray-700 text-gray-400 hover:text-white'
              : 'hover:bg-gray-100 text-gray-500 hover:text-gray-700'
            }
          `}
        >
          <Bars3Icon className={`
            h-6 w-6
            transition-all duration-200
            ${isAnimating ? 'rotate-90' : 'rotate-0'}
          `} />
        </button>

        {/* Page Title */}
        <div className={`hidden sm:block ${darkMode ? 'text-white' : 'text-gray-800'}`}>
          <h1 className="text-lg font-semibold">VaxiCare Admin</h1>
        </div>
      </div>

      <div className="flex items-center space-x-2 sm:space-x-3">
        {/* Dark Mode Toggle */}
        <button
          onClick={handleDarkModeToggle}
          className={`
            p-2.5 rounded-xl transition-all duration-300 ease-out
            relative overflow-hidden group
            ${darkMode
              ? 'bg-gray-700 text-yellow-400 hover:bg-gray-600'
              : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
            }
          `}
          title={darkMode ? "Switch to Light Mode" : "Switch to Dark Mode"}
        >
          <div className="relative w-5 h-5">
            {/* Sun Icon */}
            <SunIcon
              className={`
                w-5 h-5 absolute inset-0 transition-all duration-300
                ${darkMode
                  ? 'opacity-0 rotate-90 scale-0'
                  : 'opacity-100 rotate-0 scale-100'
                }
              `}
            />
            {/* Moon Icon */}
            <MoonIcon
              className={`
                w-5 h-5 absolute inset-0 transition-all duration-300
                ${darkMode
                  ? 'opacity-100 rotate-0 scale-100'
                  : 'opacity-0 -rotate-90 scale-0'
                }
              `}
            />
          </div>

          {/* Animated background glow */}
          <div className={`
            absolute inset-0 rounded-xl transition-opacity duration-300
            ${darkMode
              ? 'bg-gradient-to-br from-yellow-400/20 to-orange-400/20 opacity-100'
              : 'bg-gradient-to-br from-blue-400/20 to-purple-400/20 opacity-0 group-hover:opacity-100'
            }
          `} />
        </button>

        {/* Notification with hover effect */}
        <div className="hover:scale-105 transition-transform duration-200">
          <NotificationDropdown darkMode={darkMode} />
        </div>

        {/* Profile with hover effect */}
        <div className="hover:scale-105 transition-transform duration-200">
          <ProfileDropdown darkMode={darkMode} />
        </div>
      </div>
    </header>
  );
};

export default VaccineHeader;
